import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Chip,
  InputAdornment,
  Container,
  Divider,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Warning as WarningIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import Layout from '../components/layout/Layout';
import productService from '../services/productService';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';

const InventoryReports = () => {
  const { enqueueSnackbar } = useSnackbar();

  // State for filters
  const [filters, setFilters] = useState({
    sku: '',
    category: '',
    stockStatus: 'all',
  });

  // State for categories and products
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // State for summary data
  const [summaryData, setSummaryData] = useState({
    lowStock: 0,
    outOfStock: 0,
    inStock: 0,
    totalValue: 0,
    totalValueSelling: 0,
    totalValueMinSelling: 0,
    reorderCount: 0,
  });

  // State for filtered products
  const [filteredProducts, setFilteredProducts] = useState([]);

  // Add pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Add export state
  const [isExporting, setIsExporting] = useState(false);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  // Fetch categories and products on component mount
  useEffect(() => {
    fetchCategories();
    fetchProducts();
  }, []);

  // Calculate summary data whenever products change
  useEffect(() => {
    calculateSummaryData(products);
  }, [products]);

  // Function to fetch categories
  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await productService.getCategories({ size: 1000 });
      setCategories(response.content || response);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load categories');
      enqueueSnackbar('Failed to load categories', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch all products
  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await productService.getAllProducts(0, 1000);
      // Filter only active products and ensure all required fields
      const activeProducts = (response.content || [])
        .filter(product => String(product.status || 'ACTIVE').toUpperCase() === 'ACTIVE')
        .map(product => ({
          ...product,
          purchasePrice: parseFloat(product.purchasePrice || 0).toFixed(2),
          sellingPrice: parseFloat(product.sellingPrice || 0).toFixed(2),
          minSalePrice: parseFloat(product.minSalePrice || 0).toFixed(2),
          quantity: product.quantity || 0,
          reorderLevel: product.reorderLevel || 0
        }));
      setProducts(activeProducts);
    } catch (err) {
      console.error('Error fetching products:', err);
      enqueueSnackbar('Failed to load products', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Function to calculate summary data
  const calculateSummaryData = (products) => {
    const summary = products.reduce((acc, product) => {
      // Only consider active products
      if (String(product.status || 'ACTIVE').toUpperCase() === 'ACTIVE') {
        const quantity = product.quantity || 0;
        const purchasePrice = parseFloat(product.purchasePrice || 0);
        const sellingPrice = parseFloat(product.sellingPrice || 0);
        const minSalePrice = parseFloat(product.minSalePrice || 0);
        const reorderLevel = product.reorderLevel || 0;

        // Calculate stock status based on reorder level
        if (quantity === 0) {
          acc.outOfStock++;
        } else if (quantity <= reorderLevel) {
          acc.lowStock++;
        } else {
          acc.inStock++;
        }

        // Check if product needs reordering
        if (quantity <= reorderLevel) {
          acc.reorderCount++;
        }

        // Calculate total value based on purchase price
        acc.totalValue += quantity * purchasePrice;
        // Calculate total value based on selling price
        acc.totalValueSelling += quantity * sellingPrice;
        // Calculate total value based on minimum selling price
        acc.totalValueMinSelling += quantity * minSalePrice;
      }

      return acc;
    }, {
      lowStock: 0,
      outOfStock: 0,
      inStock: 0,
      totalValue: 0,
      totalValueSelling: 0,
      totalValueMinSelling: 0,
      reorderCount: 0,
    });

    setSummaryData(summary);
  };

  // Get status chip color and label
  const getStatusChip = (quantity, reorderLevel) => {
    if (quantity === 0) {
      return { color: 'error', label: 'Out of Stock' };
    } else if (quantity <= reorderLevel) {
      return { color: 'warning', label: 'Low Stock' };
    } else {
      return { color: 'success', label: 'In Stock' };
    }
  };

  // Function to handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleGenerateReport = () => {
    // Filter products based on current filters
    let filtered = [...products].filter(product =>
      String(product.status || 'ACTIVE').toUpperCase() === 'ACTIVE'
    );

    if (filters.sku) {
      filtered = filtered.filter(product =>
        product.sku?.toLowerCase().includes(filters.sku.toLowerCase())
      );
    }

    if (filters.category) {
      filtered = filtered.filter(product =>
        product.category?.id === filters.category
      );
    }

    if (filters.stockStatus !== 'all') {
      filtered = filtered.filter(product => {
        const quantity = product.quantity || 0;
        const reorderLevel = product.reorderLevel || 0;

        switch (filters.stockStatus) {
          case 'out':
            return quantity === 0;
          case 'low':
            return quantity > 0 && quantity <= reorderLevel;
          case 'in':
            return quantity > reorderLevel;
          default:
            return true;
        }
      });
    }

    // Update filtered products state
    setFilteredProducts(filtered);

    // Calculate summary for filtered products
    calculateSummaryData(filtered);
  };

  // Function to handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Function to handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportToExcel = async () => {
    try {
      setIsExporting(true);
      // Prepare export data from filteredProducts
      const exportData = filteredProducts.map((prod, idx) => ({
        'S.No': idx + 1,
        'SKU': prod.sku,
        'Product Name': prod.name,
        'Category': prod.category?.name || '-',
        'Quantity': prod.quantity,
        'Purchase Price': prod.purchasePrice,
        'Selling Price': prod.sellingPrice,
        'Min Sale Price': prod.minSalePrice,
        'Reorder Level': prod.reorderLevel,
        'Status': prod.status
      }));
      const ws = XLSX.utils.json_to_sheet(exportData);
      ws['!cols'] = [
        { wch: 5 }, { wch: 12 }, { wch: 28 }, { wch: 18 }, { wch: 10 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 10 }
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.sheet_add_aoa(ws, [
        ['Inventory Report'],
        ['Generated on: ' + new Date().toLocaleString()],
        ['Filters: ' + Object.entries(filters).map(([k, v]) => `${k}: ${v}`).join(', ')],
        ['']
      ], { origin: 'A1' });
      XLSX.utils.book_append_sheet(wb, ws, 'Inventory Report');
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `inventory_report_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      enqueueSnackbar('Inventory report exported successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to export inventory report', { variant: 'error' });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ fontSize: '0.92rem' }}>
        <Box sx={{ py: 2 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontSize: '1.3rem' }}>
            Inventory Reports
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {/* Export Button Row */}
          <Grid container justifyContent="flex-end" sx={{ mb: 2 }}>
            <Grid item>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleExportToExcel}
                disabled={isExporting || filteredProducts.length === 0}
                startIcon={isExporting ? <CircularProgress size={20} /> : <FileDownloadIcon />}
                sx={{ minWidth: 160, height: '38px', fontWeight: 600, fontSize: '0.92rem' }}
              >
                {isExporting ? 'Exporting...' : 'Export to Excel'}
              </Button>
            </Grid>
          </Grid>

          {/* Search and Filter Section */}
          <Paper sx={{ p: 2, mb: 2, fontSize: '0.92rem' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="SKU/Code"
                  variant="outlined"
                  value={filters.sku}
                  onChange={(e) => handleFilterChange('sku', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  select
                  label="Category"
                  variant="outlined"
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  disabled={loading}
                  error={!!error}
                  helperText={error}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {loading ? (
                    <MenuItem disabled>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CircularProgress size={20} />
                        <span>Loading categories...</span>
                      </Box>
                    </MenuItem>
                  ) : (
                    categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))
                  )}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  select
                  label="Stock Status"
                  variant="outlined"
                  value={filters.stockStatus}
                  onChange={(e) => handleFilterChange('stockStatus', e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="low">Low Stock</MenuItem>
                  <MenuItem value="out">Out of Stock</MenuItem>
                  <MenuItem value="in">In Stock</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleGenerateReport}
                  sx={{ height: '56px' }} // Match height with TextField
                >
                  Generate Report
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {/* Low Stock Items Card */}
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ height: '100%', minHeight: '90px' }}>
                <CardContent sx={{ p: 1.5, fontSize: '0.98rem' }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="subtitle2" component="div" gutterBottom sx={{ fontSize: '0.98rem' }}>
                        Low Stock Items
                      </Typography>
                      <Typography variant="h6" component="div" color="warning.main" sx={{ fontSize: '1.1rem' }}>
                        {summaryData.lowStock}
                      </Typography>
                    </Box>
                    <Box sx={{ color: 'warning.main' }}>
                      <WarningIcon sx={{ fontSize: 26 }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Out of Stock Card */}
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ height: '100%', minHeight: '90px' }}>
                <CardContent sx={{ p: 1.5, fontSize: '0.98rem' }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="subtitle2" component="div" gutterBottom sx={{ fontSize: '0.98rem' }}>
                        Out of Stock
                      </Typography>
                      <Typography variant="h6" component="div" color="error.main" sx={{ fontSize: '1.1rem' }}>
                        {summaryData.outOfStock}
                      </Typography>
                    </Box>
                    <Box sx={{ color: 'error.main' }}>
                      <CancelIcon sx={{ fontSize: 26 }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* In-Stock Products Card */}
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ height: '100%', minHeight: '90px' }}>
                <CardContent sx={{ p: 1.5, fontSize: '0.98rem' }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="subtitle2" component="div" gutterBottom sx={{ fontSize: '0.98rem' }}>
                        In-Stock Products
                      </Typography>
                      <Typography variant="h6" component="div" color="success.main" sx={{ fontSize: '1.1rem' }}>
                        {summaryData.inStock}
                      </Typography>
                    </Box>
                    <Box sx={{ color: 'success.main' }}>
                      <CheckCircleIcon sx={{ fontSize: 26 }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Need to Reorder Card */}
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ height: '100%', minHeight: '90px' }}>
                <CardContent sx={{ p: 1.5, fontSize: '0.98rem' }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="subtitle2" component="div" gutterBottom sx={{ fontSize: '0.98rem' }}>
                        Need to Reorder
                      </Typography>
                      <Typography variant="h6" component="div" color="secondary.main" sx={{ fontSize: '1.1rem' }}>
                        {summaryData.reorderCount}
                      </Typography>
                    </Box>
                    <Box sx={{ color: 'secondary.main' }}>
                      <WarningIcon sx={{ fontSize: 26 }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Total Value Card */}
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ height: '100%', minHeight: '90px' }}>
                <CardContent sx={{ p: 1.5, fontSize: '0.98rem' }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="subtitle2" component="div" gutterBottom sx={{ fontSize: '0.98rem' }}>
                        Total Value (Purchase)
                      </Typography>
                      <Typography variant="h6" component="div" color="info.main" sx={{ fontSize: '1.05rem' }}>
                        {formatCurrency(summaryData.totalValue)}
                      </Typography>
                    </Box>
                    <Box sx={{ color: 'info.main' }}>
                      {/* Removed MoneyIcon to eliminate $ symbol */}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Total Value (Selling Price) Card */}
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ height: '100%', minHeight: '90px' }}>
                <CardContent sx={{ p: 1.5, fontSize: '0.98rem' }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="subtitle2" component="div" gutterBottom sx={{ fontSize: '0.98rem' }}>
                        Total Value (Selling)
                      </Typography>
                      <Typography variant="h6" component="div" color="primary.main" sx={{ fontSize: '1.05rem' }}>
                        {formatCurrency(summaryData.totalValueSelling)}
                      </Typography>
                    </Box>
                    <Box sx={{ color: 'primary.main' }}>
                      {/* Removed MoneyIcon to eliminate $ symbol */}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Total Value (Min Selling Price) Card */}
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ height: '100%', minHeight: '90px' }}>
                <CardContent sx={{ p: 1.5, fontSize: '0.98rem' }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="subtitle2" component="div" gutterBottom sx={{ fontSize: '0.98rem' }}>
                        Total Value (Min Sell)
                      </Typography>
                      <Typography variant="h6" component="div" color="secondary.main" sx={{ fontSize: '1.05rem' }}>
                        {formatCurrency(summaryData.totalValueMinSelling)}
                      </Typography>
                    </Box>
                    <Box sx={{ color: 'secondary.main' }}>
                      {/* Removed MoneyIcon to eliminate $ symbol */}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Inventory Table */}
          <TableContainer component={Paper} sx={{ fontSize: '0.92rem' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Product Name</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Current Stock</TableCell>
                  <TableCell align="right">Unit Price</TableCell>
                  <TableCell>Stock Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : filteredProducts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body1" color="text.secondary">
                        No products found matching the selected filters
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  // Apply pagination to filtered products
                  filteredProducts
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((product) => {
                      const status = getStatusChip(product.quantity, product.reorderLevel);
                      return (
                        <TableRow key={product.id}>
                          <TableCell>{product.name}</TableCell>
                          <TableCell>{product.sku}</TableCell>
                          <TableCell>{product.category?.name || '-'}</TableCell>
                          <TableCell align="right">{product.quantity}</TableCell>
                          <TableCell align="right">{formatCurrency(parseFloat(product.purchasePrice))}</TableCell>
                          <TableCell>
                            <Chip
                              label={status.label}
                              color={status.color}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                )}
              </TableBody>
            </Table>
            {!loading && filteredProducts.length > 0 && (
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={filteredProducts.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            )}
          </TableContainer>
        </Box>
      </Container>
    </Layout>
  );
};

export default InventoryReports; 